import React from "react";
import {
    BarChart3,
    ClipboardList,
    Handshake,
    NotebookPen,
    Percent,
    Ship,
    UserCircle,
    Users,
} from "lucide-react";
import ReportDataGrid from "@/components/reports/layout/ReportDataGrid";
import ReportStatCard from "@/components/reports/layout/ReportStatCard";
import {
    reportAccent,
    reportAccentGreen,
    reportBodyText,
    reportCard,
    reportHeading,
    reportSectionTitle,
} from "@/components/reports/layout/reportTheme";
import {
    formatReportCurrency,
    formatReportDisplayDate,
    getExportBookingReportByLabel,
} from "@/utility/fclReportHelpers";
import {
    EXPORT_SALESPERSON_BOOKING_COLUMNS,
    EXPORT_SALESPERSON_CONTACT_COLUMNS,
    EXPORT_SALESPERSON_CONTAINER_TEUS_COLUMNS,
    EXPORT_SALESPERSON_TEUS_COLUMNS,
} from "./exportSalespersonReportColumns";

const ExportSalespersonReportView = ({
    filters,
    summary = {},
    bookingList = [],
    salesPersonTeus = [],
    contactList = [],
    containerTeusList = [],
}) => {
    const totalBooking = summary.totalBooking ?? summary.TotalBooking ?? 0;
    const totalSell = summary.totalSellRate ?? summary.TotalSellRate ?? 0;
    const totalBuy = summary.totalBuyRate ?? summary.TotalBuyRate ?? 0;
    const totalProfit = summary.totalProfit ?? summary.TotalProfit ?? 0;
    const totalTeus = summary.totalTeus ?? summary.TotalTeus ?? 0;

    const fromLabel = formatReportDisplayDate(filters?.FromDate);
    const toLabel = formatReportDisplayDate(filters?.ToDate);
    const reportByLabel = getExportBookingReportByLabel(filters?.ReportBy);

    return (
        <div className="space-y-6">
            <div className={`${reportCard} px-6 py-5 text-center`}>
                <h2 className={`text-xl font-bold ${reportHeading}`}>Booking Salesperson Report Summary</h2>
                <p className={`mt-2 text-sm ${reportBodyText}`}>
                    From <span className={`font-semibold ${reportAccentGreen}`}>{fromLabel}</span> to{" "}
                    <span className={`font-semibold ${reportAccentGreen}`}>{toLabel}</span>
                    {" · "}Filter by{" "}
                    <span className={`font-semibold ${reportAccentGreen}`}>{reportByLabel}</span>
                </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <ReportStatCard icon={NotebookPen} label="Total Booking" value={totalBooking} colorIndex={0} />
                <ReportStatCard
                    icon={Handshake}
                    label="Total Sell Amount"
                    value={formatReportCurrency(totalSell)}
                    colorIndex={1}
                />
                <ReportStatCard
                    icon={Percent}
                    label="Total Buy Amount"
                    value={formatReportCurrency(totalBuy)}
                    colorIndex={2}
                />
                <ReportStatCard
                    icon={BarChart3}
                    label="Total Profit"
                    value={formatReportCurrency(totalProfit)}
                    colorIndex={3}
                />
                <ReportStatCard
                    icon={ClipboardList}
                    label="Total Teus"
                    value={Number(totalTeus).toFixed(2)}
                    colorIndex={4}
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className={reportCard}>
                    <div className={reportSectionTitle}>
                        <UserCircle className={`h-5 w-5 ${reportAccent}`} />
                        <span className={`text-sm font-semibold ${reportHeading}`}>Sales Person TEUS</span>
                    </div>
                    <ReportDataGrid
                        gridId="export-salesperson-teus"
                        data={salesPersonTeus}
                        columns={EXPORT_SALESPERSON_TEUS_COLUMNS}
                        emptyText="No salesperson TEU data for the selected filters."
                        defaultPageSize={10}
                        paginationTitle="salespersons"
                        bare
                    />
                </div>

                <div className={reportCard}>
                    <div className={reportSectionTitle}>
                        <Users className={`h-5 w-5 ${reportAccent}`} />
                        <span className={`text-sm font-semibold ${reportHeading}`}>Contact Person Bookings</span>
                    </div>
                    <ReportDataGrid
                        gridId="export-salesperson-contacts"
                        data={contactList}
                        columns={EXPORT_SALESPERSON_CONTACT_COLUMNS}
                        emptyText="No contact person data for the selected filters."
                        defaultPageSize={10}
                        paginationTitle="contacts"
                        bare
                    />
                </div>
            </div>

            <div className={reportCard}>
                <div className={reportSectionTitle}>
                    <Ship className={`h-5 w-5 ${reportAccent}`} />
                    <span className={`text-sm font-semibold ${reportHeading}`}>
                        Container Wise TEUS Summary
                    </span>
                </div>
                <ReportDataGrid
                    gridId="export-salesperson-container-teus"
                    data={containerTeusList}
                    columns={EXPORT_SALESPERSON_CONTAINER_TEUS_COLUMNS}
                    emptyText="No container TEU summary for the selected filters."
                    defaultPageSize={10}
                    paginationTitle="container types"
                    bare
                />
            </div>

            <ReportDataGrid
                gridId="export-salesperson-booking-list"
                data={bookingList}
                columns={EXPORT_SALESPERSON_BOOKING_COLUMNS}
                title="Booking List"
                emptyText="No bookings found for the selected filters."
                defaultPageSize={10}
                paginationTitle="bookings"
            />
        </div>
    );
};

export default ExportSalespersonReportView;
